import { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { Settings, Plus, Trash2, Play, Zap, X, Check, RotateCcw, ImageIcon, Loader2, Users, Trophy, ImagePlus, ArrowLeft, Upload, Award } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card'
import ImagecompoNav from '@/components/ImagecompoNav'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs'
import { Badge } from '../components/ui/badge'
import { imagecompoAPI } from '../lib/api'
import PageHeader from '../components/PageHeader'
import { useImagecompoSocket } from '../lib/imagecompoSocket'
import { useConfirm } from '../hooks/useConfirm'

const PHASES = ['draft','submissions','live','pauze','reveal','done']

export default function ImageCompoAdminPage() {
  const confirm = useConfirm()
  const [shows, setShows] = useState([])
  const [activeShow, setActiveShow] = useState(null)
  const [subCount, setSubCount] = useState(0)
  const [entryCount, setEntryCount] = useState(0)
  // Tab-state met deeplink (?tab=uitreiking) — Regie linkt hier direct heen.
  const [searchParams, setSearchParams] = useSearchParams()
  const [tab, setTabState] = useState(() => searchParams.get('tab') || 'show')
  const setTab = (v) => {
    setTabState(v)
    const p = new URLSearchParams(searchParams)
    if (v && v !== 'show') p.set('tab', v); else p.delete('tab')
    setSearchParams(p, { replace: true })
  }

  const load = async () => {
    const r = await imagecompoAPI.listShows()
    setShows(r.data.shows || [])
    const act = r.data.shows?.find(s => s.is_active) || null
    setActiveShow(act)
    if (act) {
      try { const s = await imagecompoAPI.listAdminSubmissions(); setSubCount(s.data.submissions?.length || 0) } catch {}
      try { const e = await imagecompoAPI.listEntries(act.id); setEntryCount(e.data.entries?.length || 0) } catch {}
    }
  }
  useEffect(() => { load() }, [])
  useImagecompoSocket(load)

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <ImagecompoNav />
      <PageHeader
        icon={Settings}
        title="Image Compo · Admin"
        subtitle="Show setup, inschrijvingen, entries, punten"
        actions={<Button asChild variant="outline"><Link to="/imagecompo"><ArrowLeft className="h-4 w-4 mr-2" />Terug</Link></Button>}
      />

      <ShowsBar shows={shows} activeShow={activeShow} onChange={load} />

      {!activeShow ? (
        <Card><CardContent className="p-6 text-center text-muted-foreground">Selecteer of activeer een show.</CardContent></Card>
      ) : (
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="show">Show</TabsTrigger>
            <TabsTrigger value="submissions">Inschrijvingen {subCount > 0 && <Badge variant="secondary" className="ml-2">{subCount}</Badge>}</TabsTrigger>
            <TabsTrigger value="teams">Teams</TabsTrigger>
            <TabsTrigger value="entries">Entries {entryCount > 0 && <Badge variant="secondary" className="ml-2">{entryCount}</Badge>}</TabsTrigger>
            <TabsTrigger value="scores">Admin punten</TabsTrigger>
            {activeShow.public_scoring_enabled && <TabsTrigger value="public">Publiek punten</TabsTrigger>}
            <TabsTrigger value="uitreiking">
              Uitreiking
              {!activeShow.results_awarded_at && <span className="ml-2 h-1.5 w-1.5 rounded-full bg-peach inline-block" />}
            </TabsTrigger>
          </TabsList>
          <TabsContent value="show"><ShowTab show={activeShow} onChange={load} /></TabsContent>
          <TabsContent value="submissions"><SubmissionsTab show={activeShow} onChange={load} /></TabsContent>
          <TabsContent value="teams"><TeamsTab show={activeShow} onChange={load} /></TabsContent>
          <TabsContent value="entries"><EntriesTab show={activeShow} onChange={load} /></TabsContent>
          <TabsContent value="scores"><ScoresTab kind="admin" onChange={load} /></TabsContent>
          {activeShow.public_scoring_enabled &&
            <TabsContent value="public"><ScoresTab kind="public" onChange={load} /></TabsContent>}
          <TabsContent value="uitreiking"><UitreikingTab show={activeShow} onChange={load} /></TabsContent>
        </Tabs>
      )}
    </div>
  )
}

function ShowsBar({ shows, activeShow, onChange }) {
  const [name, setName] = useState('')
  const [edition, setEdition] = useState('')
  const [regType, setRegType] = useState('team')
  const [saving, setSaving] = useState(false)

  const create = async () => {
    if (!name) return
    setSaving(true)
    try { await imagecompoAPI.createShow({ name, edition: edition || null, registration_type: regType }); setName(''); setEdition(''); setRegType('team'); await onChange() }
    finally { setSaving(false) }
  }
  const activate = async (id) => { await imagecompoAPI.activateShow(id); await onChange() }
  const remove   = async (id) => { if (await confirm('Deze show verwijderen?')) { await imagecompoAPI.deleteShow(id); await onChange() } }
  const reset    = async (id) => { if (await confirm('Show resetten (scores + state)?')) { await imagecompoAPI.resetShow(id); await onChange() } }
  const populate = async (id) => { if (await confirm('Mock data toevoegen aan deze show?')) { await imagecompoAPI.populateShowMock(id); await onChange() } }

  return (
    <Card>
      <CardHeader><CardTitle className="text-base">Shows</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        <div className="flex gap-2 items-end">
          <div className="flex-1"><Label>Naam</Label><Input value={name} onChange={e => setName(e.target.value)} placeholder="AI Image Compo 2026" /></div>
          <div className="flex-1"><Label>Editie</Label><Input value={edition} onChange={e => setEdition(e.target.value)} placeholder="Winter Edition" /></div>
          <div>
            <Label>Formaat</Label>
            <select value={regType} onChange={e => setRegType(e.target.value)}
                    className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm">
              <option value="team">Teams</option>
              <option value="user">Users (artiestennaam)</option>
            </select>
          </div>
          <Button onClick={create} disabled={saving || !name}>
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
            Show maken
          </Button>
        </div>
        <div className="divide-y divide-border">
          {shows.map(s => (
            <div key={s.id} className="flex items-center gap-3 py-2">
              {s.is_active && <Badge className="bg-emerald-500 text-black">Actief</Badge>}
              <div className="flex-1">
                <div className="font-semibold">{s.name} {s.edition && <span className="text-muted-foreground text-sm">— {s.edition}</span>}</div>
                <div className="text-xs text-muted-foreground uppercase">{s.phase} · inschrijving: {s.submissions_open ? 'open' : 'dicht'}</div>
              </div>
              {!s.is_active && <Button size="sm" variant="outline" onClick={() => activate(s.id)}><Play className="h-4 w-4 mr-1" />Activeer</Button>}
              <Button size="sm" variant="outline" onClick={() => populate(s.id)}><Zap className="h-4 w-4 mr-1" />Mock data</Button>
              <Button size="sm" variant="outline" onClick={() => reset(s.id)}><RotateCcw className="h-4 w-4 mr-1" />Reset</Button>
              <Button size="sm" variant="outline" onClick={() => remove(s.id)}><Trash2 className="h-4 w-4 mr-1" />Delete</Button>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  )
}

function ShowTab({ show, onChange }) {
  const [saving, setSaving] = useState(false)
  const [badges, setBadges] = useState([])
  const [awarding, setAwarding] = useState(false)
  const [awardMsg, setAwardMsg] = useState(null)
  const patch = async (p) => { setSaving(true); try { await imagecompoAPI.updateShow(show.id, p); await onChange() } finally { setSaving(false) } }
  useEffect(() => {
    import('@/lib/api').then(({ badgeAPI }) => {
      badgeAPI.getAllTypes().then(r => setBadges(r.data?.badges ?? [])).catch(() => {})
    })
  }, [])
  const award = async () => {
    if (!confirm('Uitreiking: registreert de top-5 in het compo-overzicht en geeft (indien gekozen) de winnaar-badge aan de deelnemende user(s). Deze actie werkt maar één keer per show. Doorgaan?')) return
    setAwarding(true); setAwardMsg(null)
    try { await imagecompoAPI.awardResults(show.id); setAwardMsg('Uitreiking uitgevoerd'); await onChange() }
    catch (e) { setAwardMsg(e.response?.data?.error ?? e.message) }
    finally { setAwarding(false) }
  }
  return (
    <Card>
      <CardHeader><CardTitle>Show configuratie</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Naam</Label><Input defaultValue={show.name} onBlur={e => e.target.value !== show.name && patch({ name: e.target.value })} /></div>
          <div><Label>Editie</Label><Input defaultValue={show.edition || ''} onBlur={e => e.target.value !== (show.edition || '') && patch({ edition: e.target.value })} /></div>
        </div>
        <div>
          <Label>Regels & uitleg</Label>
          <textarea defaultValue={show.rules_text || ''}
                    onBlur={e => e.target.value !== (show.rules_text || '') && patch({ rules_text: e.target.value })}
                    rows={5}
                    className="w-full rounded-[4px] border border-border bg-background px-3 py-2 text-sm font-mono"
                    placeholder="Beschrijf hoe deze compo werkt. Deelnemers zien dit op de Image Compo hub." />
        </div>
        <div>
          <Label>Fase</Label>
          <div className="flex flex-wrap gap-2 mt-1">
            {PHASES.map(p => (
              <Button key={p} size="sm" variant={show.phase === p ? 'default' : 'outline'} onClick={() => patch({ phase: p })}>
                {p}
              </Button>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Label className="mb-0 w-48">Inschrijvingen open</Label>
          <Button size="sm" variant={show.submissions_open ? 'default' : 'outline'} onClick={() => patch({ submissions_open: !show.submissions_open })} disabled={saving}>
            {show.submissions_open ? 'Open' : 'Dicht'}
          </Button>
        </div>
        <div className="flex items-center gap-3">
          <Label className="mb-0 w-48">Publiekspunten gebruiken</Label>
          <Button size="sm" variant={show.public_scoring_enabled ? 'default' : 'outline'}
                  onClick={() => patch({ public_scoring_enabled: !show.public_scoring_enabled })} disabled={saving}>
            {show.public_scoring_enabled ? 'Aan' : 'Uit'}
          </Button>
          <span className="text-xs text-muted-foreground">
            {show.public_scoring_enabled
              ? 'Extra tab verschijnt — publiek punten worden opgeteld bij admin punten in de reveal'
              : 'Alleen admin punten tellen'}
          </span>
        </div>

        <div className="pt-3 border-t border-border space-y-3">
          <div>
            <Label>Winnaar-badge (optioneel)</Label>
            <select value={show.winner_badge_type_id ?? ''}
                    onChange={e => patch({ winner_badge_type_id: e.target.value ? Number(e.target.value) : null })}
                    className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm">
              <option value="">— Geen automatische badge —</option>
              {badges.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
            <p className="text-xs text-muted-foreground mt-1">
              Beheer badges op <a href="/admin/badges" className="text-acid hover:underline">/admin/badges</a>.
              Het uitreiken zelf gebeurt in de <strong>Uitreiking</strong>-tab.
            </p>
          </div>
          {/* Award-knop weggehaald: dubbel met Uitreiking-tab. Configureer
              badge hier, reik uit in de Uitreiking-tab. */}
          {awardMsg && <p className="text-sm text-muted-foreground">{awardMsg}</p>}
        </div>
      </CardContent>
    </Card>
  )
}

function SubmissionsTab({ show, onChange }) {
  const [subs, setSubs] = useState([])
  const load = () => imagecompoAPI.listAdminSubmissions().then(r => setSubs(r.data.submissions || []))
  useEffect(() => { load() }, [])
  useImagecompoSocket(load)

  const openBanner = show && !show.submissions_open && (
    <div className="mb-3 p-3 border border-yellow-500/40 bg-yellow-500/10 rounded text-sm">
      <b className="text-yellow-500">⚠ Inschrijvingen staan DICHT.</b> Users kunnen nu niet inleveren. Ga naar tab "Show" om ze open te zetten.
    </div>
  )

  const approve = async (id) => { await imagecompoAPI.approveSubmission(id); await load(); onChange?.() }
  const reject  = async (id) => { if (await confirm('Afwijzen?')) { await imagecompoAPI.rejectSubmission(id); await load(); onChange?.() } }
  const withdraw = async (id) => { if (await confirm('Intrekken voor deze user?')) { await imagecompoAPI.adminWithdrawSubmission(id); await load(); onChange?.() } }

  return (
    <Card>
      <CardHeader><CardTitle>Inschrijvingen ({subs.length})</CardTitle></CardHeader>
      <CardContent className="divide-y divide-border">
        {openBanner}
        {subs.length === 0 && <div className="text-center py-8 text-muted-foreground">Geen inschrijvingen.</div>}
        {subs.map(s => (
          <div key={s.id} className="flex items-center gap-3 py-3">
            <div className="w-16 h-16 rounded border border-border overflow-hidden flex items-center justify-center bg-muted/30 shrink-0">
              {s.image_url ? <img src={s.image_url} alt="" className="max-w-full max-h-full object-contain" />
                            : <ImageIcon className="h-6 w-6 text-muted-foreground" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold truncate">{s.title || <span className="text-muted-foreground italic">(geen titel)</span>}</div>
              <div className="text-xs text-muted-foreground">
                door {s.display_name || s.username} · team: {s.proposed_team_name || s.team_name || '—'}
              </div>
              <div className="text-xs mt-0.5">
                <Badge variant="outline">{s.status}</Badge>
                {!s.image_url && <span className="ml-2 text-yellow-500">⚠ geen image</span>}
                {!s.title && <span className="ml-2 text-yellow-500">⚠ geen titel</span>}
              </div>
            </div>
            {s.status === 'pending' && (
              <>
                <Button size="sm" variant="default" onClick={() => approve(s.id)}><Check className="h-4 w-4 mr-1" />Goedkeuren</Button>
                <Button size="sm" variant="outline" onClick={() => reject(s.id)}><X className="h-4 w-4 mr-1" />Afwijzen</Button>
              </>
            )}
            {s.status !== 'withdrawn' && (
              <Button size="sm" variant="outline" onClick={() => withdraw(s.id)}><Trash2 className="h-4 w-4 mr-1" />Intrekken</Button>
            )}
          </div>
        ))}
      </CardContent>
    </Card>
  )
}

function TeamsTab({ show }) {
  const [teams, setTeams] = useState([])
  const [name, setName] = useState(''); const [code, setCode] = useState('')
  const [persistentTeams, setPersistentTeams] = useState([])
  const [users, setUsers]                     = useState([])
  const [persistentId, setPersistentId]       = useState('')
  const [userId, setUserId]                   = useState('')
  const regType = show?.registration_type || 'team'
  const load = () => imagecompoAPI.listTeams(show.id).then(r => setTeams(r.data.teams || []))
  useEffect(() => {
    load()
    import('@/lib/api').then(({ teamsAPI, playersAPI }) => {
      teamsAPI.list().then(r => setPersistentTeams(r.data?.teams ?? [])).catch(() => {})
      playersAPI.list('').then(r => setUsers(r.data?.players ?? r.data?.users ?? [])).catch(() => {})
    })
  }, [show.id])
  useImagecompoSocket(load)

  const create = async () => {
    if (!name) return
    await imagecompoAPI.createTeam(show.id, {
      name, code: code || null, flag_colors: [],
      persistent_team_id: regType === 'team' && persistentId ? Number(persistentId) : null,
      user_id:            regType === 'user' && userId ? Number(userId) : null,
    })
    setName(''); setCode(''); setPersistentId(''); setUserId('')
    await load()
  }
  const remove = async (id) => { if (await confirm('Team verwijderen?')) { await imagecompoAPI.deleteTeam(id); await load() } }

  return (
    <Card>
      <CardHeader><CardTitle className="flex items-center gap-2"><Users className="h-4 w-4" />
        {regType === 'user' ? 'Deelnemers (artiestennaam)' : 'Teams'}
      </CardTitle></CardHeader>
      <CardContent className="space-y-3">
        <div className="flex gap-2 items-end flex-wrap">
          {regType === 'team' && (
            <div>
              <Label>Persistent team (optioneel)</Label>
              <select value={persistentId}
                      onChange={e => {
                        const id = e.target.value
                        const t = persistentTeams.find(x => String(x.id) === id)
                        setPersistentId(id); if (t) setName(t.name)
                      }}
                      className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm">
                <option value="">— Los team —</option>
                {persistentTeams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
          )}
          {regType === 'user' && (
            <div>
              <Label>User</Label>
              <select value={userId} onChange={e => setUserId(e.target.value)}
                      className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm">
                <option value="">— Kies user —</option>
                {users.map(u => <option key={u.id} value={u.id}>{u.display_name || u.username}</option>)}
              </select>
            </div>
          )}
          <div className="flex-1 min-w-[10rem]">
            <Label>{regType === 'user' ? 'Artiestennaam' : 'Team naam'}</Label>
            <Input value={name} onChange={e => setName(e.target.value)} />
          </div>
          <div className="w-24"><Label>Code</Label><Input value={code} maxLength={4} onChange={e => setCode(e.target.value.toUpperCase())} /></div>
          <Button onClick={create}><Plus className="h-4 w-4 mr-1" />Toevoegen</Button>
        </div>
        <div className="divide-y divide-border">
          {teams.map(t => (
            <div key={t.id} className="flex items-center gap-3 py-2">
              <div className="w-8 h-8 rounded border border-border flex items-center justify-center bg-muted/30 shrink-0">
                {t.logo_url ? <img src={t.logo_url} alt="" className="w-full h-full object-cover rounded" /> : <Users className="h-4 w-4 text-muted-foreground" />}
              </div>
              <div className="flex-1"><div className="font-semibold">{t.name}</div><div className="text-xs text-muted-foreground">{t.code || '—'}</div></div>
              <Button size="sm" variant="outline" onClick={() => remove(t.id)}><Trash2 className="h-4 w-4" /></Button>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  )
}

function EntriesTab({ show }) {
  const [entries, setEntries] = useState([])
  const [teams, setTeams] = useState([])
  const load = async () => {
    const [e, t] = await Promise.all([
      imagecompoAPI.listEntries(show.id),
      imagecompoAPI.listTeams(show.id),
    ])
    setEntries(e.data.entries || [])
    setTeams(t.data.teams || [])
  }
  useEffect(() => { load() }, [show.id])
  useImagecompoSocket(load)

  const uploadImage = async (id, file) => { await imagecompoAPI.uploadEntryImage(id, file); await load() }
  const setOrder    = async (id, running_order) => { await imagecompoAPI.updateEntry(id, { running_order }); await load() }
  const setTitle    = async (id, title) => { await imagecompoAPI.updateEntry(id, { title }); await load() }
  const remove      = async (id) => { if (await confirm('Entry verwijderen?')) { await imagecompoAPI.deleteEntry(id); await load() } }

  return (
    <div className="space-y-4">
      <NewEntryCard show={show} teams={teams} entries={entries} onChange={load} />
      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2"><ImagePlus className="h-4 w-4" />Entries ({entries.length})</CardTitle></CardHeader>
        <CardContent className="divide-y divide-border">
          {entries.length === 0 && <div className="text-center py-8 text-muted-foreground">Geen entries. Voeg er één toe hierboven of keur een inschrijving goed.</div>}
          {entries.map(e => (
            <div key={e.id} className="py-3 flex items-center gap-3 flex-wrap">
              <div className="w-20 h-20 rounded border border-border overflow-hidden flex items-center justify-center bg-muted/30 shrink-0">
                {e.image_url ? <img src={e.image_url} alt="" className="max-w-full max-h-full object-contain" />
                              : <ImageIcon className="h-6 w-6 text-muted-foreground" />}
              </div>
              <div className="flex-1 min-w-0">
                <Input defaultValue={e.title || ''} onBlur={ev => ev.target.value !== (e.title || '') && setTitle(e.id, ev.target.value)} placeholder="titel" />
                <div className="text-xs text-muted-foreground mt-1">{e.team_name} · <Badge variant="outline">{e.status}</Badge></div>
              </div>
              <div className="flex items-center gap-1">
                <span className="text-xs text-muted-foreground">#</span>
                <Input type="number" className="w-16" value={e.running_order} onChange={ev => setOrder(e.id, Number(ev.target.value))} />
              </div>
              <label className="cursor-pointer">
                <input type="file" accept="image/*" className="hidden" onChange={ev => ev.target.files?.[0] && uploadImage(e.id, ev.target.files[0])} />
                <Button size="sm" variant="outline" asChild><span><Upload className="h-4 w-4 mr-1" />Image</span></Button>
              </label>
              <Button size="sm" variant="outline" onClick={() => remove(e.id)}><Trash2 className="h-4 w-4" /></Button>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  )
}

function NewEntryCard({ show, teams, entries, onChange }) {
  const [teamId, setTeamId] = useState('')
  const [title, setTitle] = useState('')
  const [file, setFile] = useState(null)
  const [saving, setSaving] = useState(false)
  const usedTeamIds = new Set(entries.map(e => e.team_id))
  const availableTeams = teams.filter(t => !usedTeamIds.has(t.id))
  const nextOrder = 1 + Math.max(0, ...entries.map(e => e.running_order || 0))

  const create = async () => {
    if (!teamId || !show?.id) return
    setSaving(true)
    try {
      const r = await imagecompoAPI.createEntry(show.id, {
        team_id: Number(teamId), title, running_order: nextOrder, status: 'approved',
      })
      if (file && r?.data?.entry?.id) { await imagecompoAPI.uploadEntryImage(r.data.entry.id, file) }
      setTeamId(''); setTitle(''); setFile(null)
      await onChange()
    } finally { setSaving(false) }
  }

  return (
    <Card className="border-purple-500/40">
      <CardHeader><CardTitle className="text-sm flex items-center gap-2"><Plus className="h-4 w-4" />Handmatig entry toevoegen</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Team</Label>
            <select className="w-full h-10 rounded border border-border bg-background px-3 text-sm"
                    value={teamId} onChange={e => setTeamId(e.target.value)}>
              <option value="">Kies team…</option>
              {availableTeams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
            {availableTeams.length === 0 && <div className="text-xs text-muted-foreground mt-1">Alle teams hebben al een entry. Maak eerst een nieuw team.</div>}
          </div>
          <div>
            <Label>Titel</Label>
            <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="Bijv. 'Cyberpunk wolven'" />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Input type="file" accept="image/*" onChange={e => setFile(e.target.files?.[0] || null)} />
          <Button onClick={create} disabled={saving || !teamId || !title}>
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
            Toevoegen
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}

function ScoresTab({ kind = 'admin' }) {
  const field = kind === 'public' ? 'points_public' : 'points_admin'
  const label = kind === 'public' ? 'Publiek punten' : 'Admin punten'
  const [scores, setScores] = useState([])
  const [saving, setSaving] = useState(false)
  const load = () => imagecompoAPI.adminGetScores().then(r => setScores(r.data.scores || []))
  useEffect(() => { load() }, [kind])
  useImagecompoSocket(load)

  const setLocal = (id, v) => setScores(s => s.map(x => x.entry_id === id ? { ...x, [field]: v } : x))
  const save = async () => {
    setSaving(true)
    try {
      await imagecompoAPI.adminSaveScores(scores.map(s => ({ entry_id: s.entry_id, [field]: Number(s[field]) || 0 })))
      await load()
    } finally { setSaving(false) }
  }

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2">
          <Trophy className={`h-4 w-4 ${kind === 'public' ? 'text-cyan-400' : ''}`} />{label}
        </CardTitle>
        <Button onClick={save} disabled={saving}>{saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}Opslaan</Button>
      </CardHeader>
      <CardContent className="divide-y divide-border">
        {scores.length === 0 && <div className="text-center py-8 text-muted-foreground">Geen entries — keur eerst inschrijvingen goed.</div>}
        {scores.map(s => (
          <div key={s.entry_id} className="flex items-center gap-3 py-2">
            <div className="flex-1">
              <div className="font-semibold">{s.title}</div>
              <div className="text-xs text-muted-foreground">{s.team_name}</div>
            </div>
            <div className="text-xs text-muted-foreground text-right w-20">
              admin: <span className="tabular-nums">{s.points_admin ?? 0}</span><br />
              publiek: <span className="tabular-nums">{s.points_public ?? 0}</span>
            </div>
            <Input type="number" className={`w-24 text-right tabular-nums ${kind === 'public' ? 'border-cyan-500/40' : ''}`}
                   value={s[field] ?? 0}
                   onChange={e => setLocal(s.entry_id, e.target.value)} />
            <span className="text-xs text-muted-foreground">pt</span>
          </div>
        ))}
      </CardContent>
    </Card>
  )
}

/* Verplichte Uitreiking-tab — één plek waar de admin de hele show afsluit. */
function UitreikingTab({ show, onChange }) {
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState('')
  const doAward = async () => {
    setBusy(true); setMsg('')
    try {
      const r = await imagecompoAPI.awardResults(show.id)
      setMsg(`Uitreiking uitgevoerd — ${r.data?.awarded ?? ''} spelers kregen punten/badges.`)
      onChange?.()
    } catch (e) { setMsg(`Fout: ${e.response?.data?.error ?? e.message}`) }
    finally { setBusy(false) }
  }
  const done = !!show.results_awarded_at
  return (
    <div className="space-y-4 max-w-2xl">
      <Card style={{ borderLeft: `3px solid ${done ? '#61E294' : '#FFB57A'}` }}>
        <CardContent className="p-5 space-y-3">
          <div className="font-semibold text-lg">
            {done
              ? <span className="text-emerald-400">✓ Uitgereikt</span>
              : <span className="text-peach">Nog niet uitgereikt</span>}
          </div>
          <p className="text-sm text-muted-foreground">
            {done
              ? `Gebeurd op ${new Date(show.results_awarded_at).toLocaleString('nl-BE')}. Top-5 staat in het compo-overzicht en de winnaar heeft de badge (indien geconfigureerd).`
              : 'Registreert top-5 in het compo-overzicht en reikt de winnaar-badge uit aan de 1e plaats. Winnaar-badge configureer je in de Show-tab.'}
          </p>
          <Button size="lg" onClick={doAward} disabled={busy || done}>
            <Award className="h-4 w-4 mr-2" />
            {busy ? 'Bezig…' : done ? 'Al uitgereikt' : 'Uitreiken'}
          </Button>
          {msg && <p className="text-sm text-muted-foreground">{msg}</p>}
        </CardContent>
      </Card>
    </div>
  )
}
